import React, { useMemo, useRef, useState } from "react";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import { downloadElementAsPdf } from "@/lib/pdf";
import { Brand } from "@/components/Layout";
import { Panel, weekDays, countIn, dayXp, dayHasData, scaleOf, totalXp, spentXp, fmtShort, DAY_LETTERS } from "./scShared";

const INK = "#4A3B36", SOFT = "#6E5C53", BEIGE = "#EFE7DD";
const TONES = {
  vert: { bar: "#7CC6A6", bg: "#E4F5EC", fg: "#2F7A55" },
  orange: { bar: "#F2B84B", bg: "#FFF3D6", fg: "#B8781A" },
  rouge: { bar: "#E0705C", bg: "#FDE4DF", fg: "#B4483A" },
};

export const buildSynthese = (state, wk, week) => {
  const keys = weekDays(wk);
  const scale = scaleOf(state);
  const perDay = keys.map((k, i) => {
    const d = state.days[k]; const has = dayHasData(d); const x = has ? dayXp(d, state) : { enfant: 0, parent: 0 };
    return { key: k, letter: DAY_LETTERS[i], has, xp: x, energie: has ? scale.find((s) => s.id === d.energie) || null : null, rappels: d?.rappelsEvites || 0 };
  });
  const days = keys.map((k) => state.days[k]).filter(dayHasData);
  const dispo = totalXp(state).enfant - spentXp(state);
  const claims = state.claims.filter((c) => keys.includes(c.date));
  const rewards = [...state.rewards].sort((a, b) => a.xp - b.xp).map((r) => ({ ...r, ratio: r.xp ? Math.min(1, dispo / r.xp) : 1, ok: dispo >= r.xp, won: claims.filter((c) => c.rewardId === r.id).length }));
  const lab = Object.entries(week.lab || {}).map(([a, v]) => ({ label: a, enfant: v.enfant || 0, parent: v.parent || 0 })).filter((x) => x.enfant + x.parent > 0).sort((a, b) => b.enfant + b.parent - a.enfant - a.parent).slice(0, 3);
  return {
    keys, perDay, n: days.length, dispo, claims, rewards, lab,
    xp: perDay.reduce((a, d) => ({ e: a.e + d.xp.enfant, p: a.p + d.xp.parent }), { e: 0, p: 0 }),
    rappels: perDay.reduce((t, d) => t + d.rappels, 0),
    maxXp: Math.max(1, ...perDay.map((d) => d.xp.enfant + d.xp.parent)),
    facilitants: countIn(days, "pouvoir"), empechants: countIn(days, "obstacle"), bloquants: countIn(days, "boss"),
  };
};

const Stat = ({ emoji, value, label, tone, testid }) => (
  <div className="rounded-2xl px-3 py-2 flex items-center gap-2" style={{ background: tone.bg }} data-testid={testid}>
    <span className="text-2xl">{emoji}</span>
    <div><div className="text-lg font-black leading-none" style={{ color: tone.fg }}>{value}</div><div className="text-[11px]" style={{ color: SOFT }}>{label}</div></div>
  </div>
);

const EnergyStrip = ({ s, testid }) => (
  <div className="rounded-2xl bg-white border p-3" style={{ borderColor: BEIGE }} data-testid={testid}>
    <div className="text-sm font-bold mb-2" style={{ color: INK }}>⚡ Ma semaine en énergie <span className="text-xs font-normal" style={{ color: SOFT }}>— humeur du soir et XP gagnés chaque jour</span></div>
    <div className="grid grid-cols-7 gap-1.5 items-end" style={{ height: 96 }}>
      {s.perDay.map((d) => {
        const tot = d.xp.enfant + d.xp.parent;
        return (
          <div key={d.key} className="flex flex-col items-center justify-end h-full gap-1" data-testid={`${testid}-${d.key}`}>
            <span className="text-lg leading-none">{d.energie ? d.energie.emoji : d.has ? "✨" : "·"}</span>
            <div className="w-full rounded-t-lg" style={{ height: `${d.has ? Math.max(8, (tot / s.maxXp) * 48) : 4}px`, background: d.energie ? d.energie.color : d.has ? TONES.orange.bar : BEIGE }} />
            <span className="text-[10px] font-bold" style={{ color: d.has ? INK : SOFT }}>{d.letter}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const Bars = ({ title, emoji, items, tone, n, empty, testid }) => (
  <div className="rounded-2xl p-3" style={{ background: tone.bg }} data-testid={testid}>
    <div className="text-sm font-bold mb-2" style={{ color: INK }}>{emoji} {title}</div>
    {items.length === 0 ? <p className="text-xs italic" style={{ color: SOFT }}>{empty}</p> : (
      <div className="space-y-1.5">
        {items.slice(0, 4).map(([k, c]) => (
          <div key={k} className="text-xs" style={{ color: INK }}>
            <div className="flex justify-between gap-2"><span className="truncate">{k}</span><span className="font-bold shrink-0" style={{ color: tone.fg }}>{c} soir{c > 1 ? "s" : ""}</span></div>
            <div className="h-2 rounded-full bg-white/70 overflow-hidden mt-0.5"><div className="h-full rounded-full" style={{ width: `${(c / Math.max(1, n)) * 100}%`, background: tone.bar }} /></div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const Ladder = ({ s, testid }) => (
  <div className="rounded-2xl p-3" style={{ background: TONES.orange.bg }} data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className="text-sm font-bold" style={{ color: INK }}>🎁 Les récompenses et leur barème</div>
      <div className="text-xs" style={{ color: SOFT }}>XP disponibles : <strong className="text-base" style={{ color: TONES.orange.fg }} data-testid={`${testid}-dispo`}>{s.dispo}</strong></div>
    </div>
    {s.rewards.length === 0 ? <p className="text-xs italic" style={{ color: SOFT }}>Aucune récompense dans le menu — ajoutez-en dans « XP & récompenses ».</p> : (
      <div className="space-y-1.5">
        {s.rewards.map((r) => (
          <div key={r.id} className="text-xs" style={{ color: INK }} data-testid={`${testid}-${r.id}`}>
            <div className="flex justify-between gap-2">
              <span className="truncate">{r.won > 0 ? "🎉" : r.ok ? "🔓" : "🔒"} {r.label}</span>
              <span className="font-bold shrink-0" style={{ color: TONES.orange.fg }}>{r.xp} XP{r.won > 0 ? ` · gagnée ×${r.won}` : r.ok ? " · à portée" : ` · encore ${r.xp - s.dispo}`}</span>
            </div>
            <div className="h-2 rounded-full bg-white/70 overflow-hidden mt-0.5"><div className="h-full rounded-full" style={{ width: `${r.ratio * 100}%`, background: r.won > 0 ? TONES.vert.bar : TONES.orange.bar }} /></div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const SyntheseBody = ({ s, state, testid = "sc-synthese" }) => {
  const child = state.setup.childName || "l'enfant";
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat emoji="🌙" value={`${s.n}/7`} label="missions du soir" tone={TONES.vert} testid={`${testid}-stat-missions`} />
        <Stat emoji="⭐" value={`${s.xp.e} XP`} label={child} tone={TONES.orange} testid={`${testid}-stat-xp-enfant`} />
        <Stat emoji="🤝" value={`${s.xp.p} XP`} label="coéquipier" tone={{ bg: "#E3F4F2", fg: "#237572" }} testid={`${testid}-stat-xp-parent`} />
        <Stat emoji="🔕" value={s.rappels} label="rappel(s) évité(s)" tone={TONES.rouge} testid={`${testid}-stat-rappels`} />
      </div>
      <EnergyStrip s={s} testid={`${testid}-energie`} />
      <Ladder s={s} testid={`${testid}-rewards`} />
      <div className="grid sm:grid-cols-3 gap-2">
        <Bars title="Ce qui a facilité" emoji="🟢" items={s.facilitants} tone={TONES.vert} n={s.n} empty="Aucune mission du soir cette semaine." testid={`${testid}-facilitants`} />
        <Bars title="Ce qui a empêché" emoji="🟠" items={s.empechants} tone={TONES.orange} n={s.n} empty="Rien de noté." testid={`${testid}-empechants`} />
        <Bars title="Ce qui a bloqué" emoji="🔴" items={s.bloquants} tone={TONES.rouge} n={s.n} empty="Aucune montagne infranchissable — bravo !" testid={`${testid}-bloquants`} />
      </div>
      {s.lab.length > 0 && (
        <div className="rounded-2xl p-3" style={{ background: "#E3F4F2" }} data-testid={`${testid}-lab`}>
          <div className="text-sm font-bold mb-1" style={{ color: INK }}>🧪 Les astuces ou techniques les mieux notées</div>
          <div className="flex flex-wrap gap-2 text-xs" style={{ color: INK }}>{s.lab.map((l) => <span key={l.label} className="rounded-full bg-white px-3 py-1">{l.label} · {child} {"★".repeat(l.enfant)} · parent {"★".repeat(l.parent)}</span>)}</div>
        </div>
      )}
    </div>
  );
};

export default function ScSynthese({ state, wk, week }) {
  const s = useMemo(() => buildSynthese(state, wk, week), [state, wk, week]);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const exportPdf = async () => {
    setBusy(true);
    try { await downloadElementAsPdf(ref.current, `super-cerveau-synthese-${wk}.pdf`); toast.success("Synthèse de la semaine exportée en PDF"); }
    catch { toast.error("Export PDF impossible"); } finally { setBusy(false); }
  };
  return (
    <Panel title="🎉 La synthèse de la semaine" sub="Un coup d'œil ludique sur la semaine : l'énergie, les XP et les récompenses, ce qui a facilité, empêché ou bloqué." color="boss" testid="sc-synthese">
      <SyntheseBody s={s} state={state} />
      <div className="flex justify-end mt-4">
        <button onClick={exportPdf} disabled={busy} data-testid="sc-synthese-pdf" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#8B6FC0] text-white text-sm font-bold shadow-sm hover:brightness-105 active:scale-95 transition-all disabled:opacity-60"><FileDown size={16} /> {busy ? "Préparation…" : "Télécharger la synthèse (PDF)"}</button>
      </div>
      <div className="fixed -left-[9999px] top-0" aria-hidden="true">
        <div ref={ref} className="bg-white p-8" style={{ width: 794, color: INK, fontFamily: "Georgia, serif" }} data-testid="sc-synthese-pdf-content">
          <div className="text-center mb-5"><Brand /></div>
          <h1 className="text-2xl font-bold text-center mb-1">Mission « Super-Cerveau » — Synthèse de la semaine</h1>
          <p className="text-center text-sm mb-5">Semaine du {fmtShort(s.keys[0])} au {fmtShort(s.keys[6])} · Joueur : {state.setup.childName || "—"} · Coéquipier : {state.setup.parentName || "—"}</p>
          <SyntheseBody s={s} state={state} testid="sc-synthese-print" />
          <p className="mt-6 text-[10px] text-center" style={{ color: SOFT }}>M2HAT Psychologie — Mission « Super-Cerveau » · Document généré localement, aucune donnée n'est transmise.</p>
        </div>
      </div>
    </Panel>
  );
}
